import React, { useRef } from "react";
import { diffPriceDigits, priceDirection } from "./priceDigits";
import "./LivePriceTicker.css";

/**
 * Large live price where only the digits that changed roll in, flashing the up or
 * down colour. Pass the latest close as `value`; the component remembers the
 * previous one itself.
 */
export default function LivePriceTicker({
  value,
  format,
  color,
  upColor = "#5fe0a8",
  downColor = "#ff7d8c",
  fontSize = "clamp(28px, 3.2vw, 44px)",
}) {
  const text = Number.isFinite(value) ? format(value) : "-";
  const memo = useRef({ text: "", value: NaN, flip: false, dir: null, digits: [] });

  // Recompute only when the shown text actually changes, so unrelated re-renders of
  // the chart don't restart the animation.
  if (memo.current.text !== text) {
    const dir = priceDirection(memo.current.value, value);
    memo.current = {
      text,
      value,
      flip: !memo.current.flip,
      dir: dir || memo.current.dir,
      digits: diffPriceDigits(memo.current.text, text),
    };
  }

  const { digits, flip, dir } = memo.current;
  const rollClass = flip ? "lwt-live-price__digit--roll-a" : "lwt-live-price__digit--roll-b";

  return (
    <div
      className="lwt-live-price"
      aria-label={`Last price ${text}`}
      style={{
        fontSize,
        color,
        "--lwt-flash": dir === "down" ? downColor : upColor,
      }}
    >
      {digits.map((d, i) => (
        <span
          key={i}
          aria-hidden="true"
          className={d.changed ? `lwt-live-price__digit ${rollClass}` : "lwt-live-price__digit"}
        >
          {d.char}
        </span>
      ))}
    </div>
  );
}
